/**
 * 创建设定对话框组件
 *
 * 提供用户友好的界面来创建新设定，支持三级设定系统（全文级/卷级/章节级）
 * 包含完整的表单验证、错误处理和用户反馈
 *
 * 安全特性：
 * - Markdown内容大小限制防止DoS攻击
 * - WebSocket安全传输
 * - 组件卸载后状态更新防护
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { Setting, SettingCreate } from '../../types/setting';
import { SettingLevel, SettingType } from '../../types/setting';
import { SettingService, ValidationError, DuplicateNameError } from '../../services/setting';
import { useAuthStore } from '../../store/authStore';

// 初始表单数据
const INITIAL_FORM_DATA: Omit<SettingCreate, 'content'> = {
  name: '',
  level: SettingLevel.GLOBAL,
  scope: '-1',
  setting_type: SettingType.OUTLINE
};

// 作用范围验证函数
const validateVolumeScope = (scope: string): boolean => {
  if (!scope) return false;
  try {
    const parsed = JSON.parse(scope);
    return Array.isArray(parsed);
  } catch {
    return false;
  }
};

const validateChapterScope = (scope: string): boolean => {
  const num = parseInt(scope, 10);
  return !isNaN(num) && num > 0;
};

// SVG图标常量
const CLOSE_ICON = (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const LOADING_SPINNER = (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
      fill="none"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const ERROR_ICON = (
  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// 辅助函数
const LEVEL_LABELS: Record<SettingLevel, string> = {
  [SettingLevel.GLOBAL]: '全文级',
  [SettingLevel.VOLUME]: '卷级',
  [SettingLevel.CHAPTER]: '章节级'
};

const TYPE_LABELS: Record<SettingType, string> = {
  [SettingType.CHARTER]: '宪章',
  [SettingType.OUTLINE]: '大纲',
  [SettingType.DETAIL]: '细纲',
  [SettingType.SUMMARY]: '摘要',
  [SettingType.CHARACTER]: '人物',
  [SettingType.SUPPORTING]: '配角',
  [SettingType.LOCATION]: '地点',
  [SettingType.ITEM]: '物品'
};

const getLevelLabel = (level: SettingLevel): string => LEVEL_LABELS[level];
const getTypeLabel = (type: SettingType): string => TYPE_LABELS[type];

// 表单验证函数
function validateFormData(data: Omit<SettingCreate, 'content'>): Record<string, string> {
  const errors: Record<string, string> = {};
  const trimmedName = data.name.trim();

  // 名称验证
  if (!trimmedName || trimmedName.length > 100) {
    errors.name = '设定名称长度必须在1-100字符之间';
  }

  // 作用范围验证
  if (data.level === SettingLevel.VOLUME) {
    if (!data.scope || !validateVolumeScope(data.scope)) {
      errors.scope = '卷级设定作用范围必须是有效JSON数组格式，如\'[1-20,34,35]\'';
    }
  } else if (data.level === SettingLevel.CHAPTER) {
    if (!data.scope || !validateChapterScope(data.scope)) {
      errors.scope = '章节号必须是大于0的整数';
    }
  }
  // 全文级的scope自动设为"-1"，不需要验证

  return errors;
}

// 表单字段组件
interface FormFieldProps {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

function FormField({ label, error, required, children }: FormFieldProps) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-900 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

// 组件Props接口
interface CreateSettingModalProps {
  /** 工作区ID */
  workspaceId: number;
  /** 对话框显示状态 */
  isOpen: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 创建成功回调 */
  onSuccess?: (setting: Setting) => void;

/** 可选: SettingService实例 */
  settingService?: SettingService;
  /** 当前工作区的所有设定，用于前端验证 */
  existingSettings?: Setting[];
}

export const CreateSettingModal: React.FC<CreateSettingModalProps> = ({
  workspaceId,
  isOpen,
  onClose,
  onSuccess,
  settingService: externalSettingService,
  existingSettings = []
}: CreateSettingModalProps) => {
  // 使用ref跟踪组件是否已挂载，防止组件卸载后的状态更新
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 获取 token
  const { token } = useAuthStore();

  // 表单状态
  const [formData, setFormData] = useState<Omit<SettingCreate, 'content'>>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // 服务实例懒初始化
  const settingService = useRef<SettingService | null>(null);

  const getSettingService = (): SettingService => {
    if (!settingService.current) {
      settingService.current = externalSettingService || new SettingService(undefined, token || undefined, workspaceId);
    }
    return settingService.current;
  };

  // 当级别改变时，自动设置作用范围
  useEffect(() => {
    if (formData.level === SettingLevel.GLOBAL) {
      setFormData(prev => ({ ...prev, scope: '-1' }));
    } else if (formData.level === SettingLevel.VOLUME) {
      setFormData(prev => ({ ...prev, scope: '' }));
    } else if (formData.level === SettingLevel.CHAPTER) {
      setFormData(prev => ({ ...prev, scope: '' }));
    }
  }, [formData.level]);

  // 当对话框打开时，重置表单
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // 处理字段变化
  const handleFieldChange = useCallback(<K extends keyof SettingCreate>(
    field: K,
    value: SettingCreate[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // 清除该字段的错误
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // 处理表单提交
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // 清除表单级错误
    setFormError(null);

    // 验证表单
    const validationErrors = validateFormData(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // 前端验证：检查设定名称是否已存在
    const duplicateSetting = existingSettings.find(s => s.name.toLowerCase() === formData.name.toLowerCase());
    if (duplicateSetting) {
      setErrors({ name: `设定名称 "${formData.name}" 已存在，请使用其他名称` });
      return;
    }

    // 构建完整的表单数据，content 设为占位内容
    const fullFormData: SettingCreate = {
      ...formData,
      content: `${formData.name}\n\n暂无详细内容，请在编辑器中添加。`
    };

    setIsSubmitting(true);

    try {
      const newSetting = await getSettingService().createSetting(workspaceId, fullFormData);

      // 检查组件是否仍然挂载
      if (!isMountedRef.current) return;

      // 创建成功
      onSuccess?.(newSetting);
      onClose();
      resetForm();
    } catch (error) {
      // 记录详细错误到日志（不上报到用户）
      console.error('[CreateSettingModal] 创建设定失败:', error);

      // 检查组件是否仍然挂载
      if (!isMountedRef.current) return;

      if (error instanceof DuplicateNameError) {
        setErrors({ name: '设定名称已存在' });
      } else if (error instanceof ValidationError) {
        setFormError(error.message);
      } else {
        setFormError('创建失败，请检查网络连接后重试');
      }
    } finally {
      // 检查组件是否仍然挂载
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [formData, workspaceId, getSettingService, onSuccess, onClose, existingSettings]);

  // 重置表单
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setFormError(null);
  }, []);

  // 处理遮罩点击
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // 不显示时返回null
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* 对话框标题 */}
        <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">创建新设定</h2>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="关闭"
            >
              {CLOSE_ICON}
            </button>
          </div>
        </div>

        {/* 表单内容 */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* 表单级错误 */}
          {formError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                {ERROR_ICON}
                <p className="ml-2 text-red-700">{formError}</p>
              </div>
            </div>
          )}

          {/* 设定名称 */}
          <FormField label="设定名称" error={errors.name} required>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="请输入设定名称"
              maxLength={100}
              disabled={isSubmitting}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white
                ${errors.name ? 'border-red-300' : 'border-gray-200'}
                disabled:bg-gray-100 disabled:cursor-not-allowed`}
            />
            <div className="mt-1 text-xs text-gray-700">
              {formData.name.length} / 100
            </div>
          </FormField>

          {/* 设定级别 */}
          <FormField label="设定级别" required>
            <select
              value={formData.level}
              onChange={(e) => handleFieldChange('level', e.target.value as SettingLevel)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value={SettingLevel.GLOBAL}>{getLevelLabel(SettingLevel.GLOBAL)}</option>
              <option value={SettingLevel.VOLUME}>{getLevelLabel(SettingLevel.VOLUME)}</option>
              <option value={SettingLevel.CHAPTER}>{getLevelLabel(SettingLevel.CHAPTER)}</option>
            </select>
          </FormField>

          {/* 作用范围 */}
          {formData.level === SettingLevel.GLOBAL && (
            <FormField label="作用范围" required>
              <input
                type="text"
                value="整个工作区"
                disabled
                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-700 cursor-not-allowed"
              />
              <div className="mt-1 text-xs text-gray-700">
                全文级设定对整个工作区生效
              </div>
            </FormField>
          )}

          {formData.level === SettingLevel.VOLUME && (
            <FormField label="作用范围（章节范围）" error={errors.scope} required>
              <input
                type="text"
                value={formData.scope}
                onChange={(e) => handleFieldChange('scope', e.target.value)}
                placeholder="[1-20,34,35]"
                disabled={isSubmitting}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white
                  ${errors.scope ? 'border-red-300' : 'border-gray-200'}
                  disabled:bg-gray-100 disabled:cursor-not-allowed`}
              />
              <div className="mt-1 text-xs text-gray-700">
                支持JSON数组格式，如：[1-20,34,35] 表示第1-20章和第34、35章
              </div>
            </FormField>
          )}

          {formData.level === SettingLevel.CHAPTER && (
            <FormField label="作用范围（章节号）" error={errors.scope} required>
              <input
                type="number"
                value={formData.scope}
                onChange={(e) => handleFieldChange('scope', e.target.value)}
                placeholder="19"
                min={1}
                disabled={isSubmitting}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white
                  ${errors.scope ? 'border-red-300' : 'border-gray-200'}
                  disabled:bg-gray-100 disabled:cursor-not-allowed`}
              />
              <div className="mt-1 text-xs text-gray-700">
                输入单个章节号，如：19
              </div>
            </FormField>
          )}

          {/* 设定类型 */}
          <FormField label="设定类型" required>
            <select
              value={formData.setting_type}
              onChange={(e) => handleFieldChange('setting_type', e.target.value as SettingType)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value={SettingType.CHARTER}>{getTypeLabel(SettingType.CHARTER)}</option>
              <option value={SettingType.OUTLINE}>{getTypeLabel(SettingType.OUTLINE)}</option>
              <option value={SettingType.DETAIL}>{getTypeLabel(SettingType.DETAIL)}</option>
              <option value={SettingType.SUMMARY}>{getTypeLabel(SettingType.SUMMARY)}</option>
              <option value={SettingType.CHARACTER}>{getTypeLabel(SettingType.CHARACTER)}</option>
              <option value={SettingType.SUPPORTING}>{getTypeLabel(SettingType.SUPPORTING)}</option>
              <option value={SettingType.LOCATION}>{getTypeLabel(SettingType.LOCATION)}</option>
              <option value={SettingType.ITEM}>{getTypeLabel(SettingType.ITEM)}</option>
            </select>
          </FormField>

          {/* 操作按钮 */}
          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-md font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-ink-800 hover:bg-ink-700 text-white rounded-md font-medium shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  {LOADING_SPINNER}
                  创建中...
                </>
              ) : (
                '创建设定'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSettingModal;
